import { useEffect, useState } from "react";
import { setReferrerQuery } from "../../stores/referrerStore";

const SEARCH_CALLOUT_STORAGE_KEY = "semanticCalloutSeen";

/**
 * Manages the visibility of the search callout/attention component.
 * Shows callout on first visit (tracked via localStorage) and hides when query is entered.
 *
 * @param queryValue - The current search query value
 * @returns Object containing callout visibility state and dismiss handler
 */
export function useSearchCallout(queryValue: string) {
	const [shouldShowCallout, setShouldShowCallout] = useState(false);

	// Initialize callout visibility on mount and capture referrer query
	useEffect(() => {
		if (typeof window === "undefined") return;

		const params = new URLSearchParams(window.location.search);
		const initialQuery = params.get("q");
		const hasQueryInUrl = Boolean(initialQuery?.trim());

		// Capture the referrer URL to extract the query parameter
		// This is used for the back button to navigate with the correct query
		const referrer = document.referrer;
		if (referrer) {
			try {
				const referrerUrl = new URL(referrer);
				// Check if the referrer is from the search page
				if (referrerUrl.pathname.includes("/recommerce/forsale/search")) {
					const referrerQuery = referrerUrl.searchParams.get("q");
					if (referrerQuery?.trim()) {
						setReferrerQuery(referrerQuery);
					}
				}
			} catch (e) {
				console.warn("Failed to parse referrer URL:", e);
			}
		}

		if (!hasQueryInUrl) {
			try {
				const hasSeenCallout = window.localStorage.getItem(SEARCH_CALLOUT_STORAGE_KEY) === "true";
				if (!hasSeenCallout) {
					setShouldShowCallout(true);
					window.localStorage.setItem(SEARCH_CALLOUT_STORAGE_KEY, "true");
				}
			} catch {
				// If localStorage is unavailable, fall back to showing the callout per session
				setShouldShowCallout(true);
			}
		}
	}, []);

	// Hide callout when user types
	useEffect(() => {
		if (queryValue.trim()) {
			setShouldShowCallout(false);
		}
	}, [queryValue]);

	const showCallout = queryValue.length === 0 && shouldShowCallout;

	const handleDismiss = () => {
		setShouldShowCallout(false);
	};

	return { showCallout, handleDismiss };
}
